import React, { useRef } from 'react';
import OverallTableCo from '@/pages/dashboard/components/OverallTableCo';
import {
  PagingQuery,
  TableProps,
} from '@/pages/dashboard/components/OverallTableCo/types';
import { IDbbSegmentOption } from '../DbbSegment';
import { IRadioOption } from '../TabsAndRadio';
import { ComponentMetricsItemType } from '@/pages/dashboard/components/PageContainer/type';
import { getName } from '@home/utils/common';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { GameRender, CountryRender } from './DimensionRender';
import SvgIcon from '@home/components/SvgIcon';
import dateTimeFormat from '@home/utils/dbDataFormat/dateTimeFormat';
import './index.scss';

export interface IBenchMarkTableProps extends TableProps {
  // 当前激活tab
  activeTab?: IDbbSegmentOption;
  // 当前激活单选
  activeRadio?: IRadioOption;
  // 单选请求值的属性名
  radioValueKey?: string;
  // 指标列配置
  metricColumnList?: ComponentMetricsItemType[];
  // 维度列宽度
  dimColumnWidth?: number;
  // 维度列冻结
  dimColumnFixed?: 'left' | 'right';
  // 开启宽度固定，最后列留白
  openLastColumnAutoWidth?: boolean;
  [key: string]: any;
}

export default function BenchMarkTable(props: IBenchMarkTableProps) {
  const {
    activeTab,
    activeRadio,
    radioValueKey,
    config,
    metricColumnList,
    filterVal,
    filterLoading,
    platformMap,
    countryMap,
    dimColumnWidth,
    filterError,
    openLastColumnAutoWidth = true,
    filterConfig,
    selectedValue,
    dimColumnFixed,
    hasPercentile,
    ...params
  } = props || {};

  const {
    dimensionConfig = {},
    slotConfig = {},
    downConfig = {},
    otherParams = {},
    componentMetrics = [],
    customColumnsBeforeDim = [],
    ...paramsConfig
  } = config || {};

  const { isEN } = useLocal();

  const tableRef = useRef<HTMLDivElement>(null);

  const { value = 'game', text } = activeTab || {};
  const { value: radioValue } = activeRadio || {};
  const { textCn, textEn } = text || {};

  // 根据tab展示列
  const showColumns = (
    customColumnsBeforeDim?.filter((item: any) => {
      // 增加控制显示隐藏列
      if (Array.isArray(item.showTabs)) {
        return item.showTabs.includes(value);
      }
      return true;
    }) || []
  ).map((item: any) => {
    const { render, valueKeyEn, valueKeyCn, filter, formatRule } = item;
    if (render) {
      return item;
    }
    // 展示过滤器
    if (filter) {
      return {
        ...item,
        headerRender: (record: { nameEn: any; nameCn: any; }) => {
          const header = getName(isEN, record?.nameEn, record?.nameCn);
          return <div>
            {header}
            <SvgIcon
              className={
                selectedValue !== '255'
                  ? 'table-change-actionicon'
                  : 'table-change-icon'
              }
              type="funnel"
            />
          </div>;
        },
        render: (row: any) => getName(isEN, get(row, valueKeyEn), get(row, valueKeyCn)) || '-',
      };
    }
    // 中英文取值
    if (valueKeyCn || valueKeyEn) {
      return {
        ...item,
        render: (row: any) => {
          let value = getName(isEN, get(row, valueKeyEn), get(row, valueKeyCn));
          if (formatRule) {
            // 暂时只支持日期格式转换，等home仓dataformat上线后，再替换
            value = dateTimeFormat(value, formatRule);
          }
          return value;
        }
      };
    }
  });

  // 表格固定列
  const customColumns = [
    {
      columnKey: 'index',
      valueKey: '',
      rainbowKey: '',
      nameEn: '#',
      nameCn: '#',
      enWidth: 80,
      cnWidth: 80,
      sorter: false,
      fixed: dimColumnFixed,
      render: (
        row: any,
        valueKey: string,
        paging: PagingQuery,
        index: number,
      ) => {
        const { pageIndex = 1, pageSize = 10 } = paging || {};
        return `${(pageIndex - 1) * pageSize + index + 1}`;
      },
    },
    {
      columnKey: 'dimension',
      valueKey: value,
      nameEn: String(textEn),
      nameCn: String(textCn),
      enWidth: dimColumnWidth,
      cnWidth: dimColumnWidth,
      sorter: false,
      fixed: dimColumnFixed,
      render: (row: any) => {
        if (value === 'game') {
          return (
            <GameRender
              data={row}
              frameType={filterVal?.frame_type}
              filterVal={filterVal}
            />
          );
        }
        if (value === 'country') {
          const code: string = row[value];
          const { name_ch, name_en } = countryMap[code?.toLowerCase()] || {};
          return (
            <CountryRender
              value={code}
              name={getName(isEN, name_en, name_ch)}
            />
          );
        }
        if (value === 'platform') {
          const { name_ch, name_en } = platformMap[row[value]] || {};
          return getName(isEN, name_en, name_ch);
        }
        return row[value];
      },
    },
    ...showColumns,
  ];

  const metricsList = [...componentMetrics, ...(metricColumnList || [])];
  // 统计全部宽度，计算是否超过外容器宽度
  let isScroll = true;
  if (openLastColumnAutoWidth) {
    let tableWidth = tableRef.current?.clientWidth;
    if (!tableWidth) {
      tableWidth = Number(sessionStorage.getItem('BenchMarkTableWidth'));
    } else {
      sessionStorage.setItem('BenchMarkTableWidth', String(tableWidth));
    }
    let totalWidthEn = 0;
    let totalWidthCn = 0;
    [...customColumns, ...metricsList].forEach((item) => {
      // 指标类型+40间距
      totalWidthCn =
        totalWidthCn + (item.cnWidth || 0) + (item.metricKey ? 40 : 0);
      totalWidthEn =
        totalWidthEn + (item.enWidth || 0) + (item.metricKey ? 40 : 0);
    });
    isScroll = (isEN ? totalWidthEn : totalWidthCn) > tableWidth;
  }

  // 确认是否打开最后一列自适应宽度
  const isOpenLastColAutoWidth = openLastColumnAutoWidth && !isScroll;
  // 用于做留白处理的列
  const autoColumn = [];
  if (isOpenLastColAutoWidth) {
    autoColumn.push({
      columnKey: '',
      valueKey: '',
      rainbowKey: '',
      nameEn: '',
      nameCn: '',
      sorter: false,
      render: () => '',
    });
  }

  // 格式化参数
  const formatParams = (params: any, isDown?: boolean) => {
    // 解构除去多余参数
    const {
      aggregates,
      end_date,
      start_date,
      period,
      show_dod,
      show_sdlw,
      metrics,
      ...apiParams
    } = params;

    const downDimensionList = [
      { name_code: value, name_en: textEn, name_cn: textCn },
    ];
    // 下载增加对应的列
    if (showColumns?.length > 0) {
      downDimensionList.push(
        ...showColumns.map((item) => {
          const { nameEn, nameCn, columnKey } = item;
          return { name_code: columnKey, name_en: nameEn, name_cn: nameCn };
        }),
      );
    }
    return {
      ...apiParams,
      filters: filterVal?.filters,
      accumulation: {
        by_game: value === 'game',
        by_genre: value === 'genre',
        by_region: value === 'region',
        by_country: value === 'country',
        by_platform: value === 'platform',
      },
      frame_type: filterVal?.frame_type,
      is_page: !isDown,
      dimension_info: isDown ? downDimensionList : undefined,
      // 百分位非指标，需过滤掉
      metrics: otherParams?.is_show_percentile ? metrics?.filter((key: string) => !key.includes('_percentile')) : metrics
    };
  };

  const newProps = {
    filterVal,
    filterLoading,
    isError: filterError,
    config: {
      titleEn: ' ',
      titleCn: ' ',
      // 关闭copy面板
      isNewSort: false,
      // 取消日期列
      hasDateColumn: false,
      // 默认拿第一个指标排序
      defaultSort: [
        {
          by: metricColumnList?.[0]?.metricKey || '',
          order: undefined,
          // 设置默认倒序
          isDefaultDesc: true,
        },
      ],
      // 隐藏维度行
      dimensionConfig: { showDimList: false, ...dimensionConfig },
      // 隐藏下载
      slotConfig: {
        // isShowdownloadConfig: false,
        // 隐藏指标设置
        isShowMetricsSettings: false,
        // 隐藏统计指标
        isShowValueType: false,
        // 隐藏热力图
        isShowProgressBar: false,
        ...slotConfig,
      },
      downConfig: {
        // 隐藏sum、avg
        showModalDataStat: false,
        // 隐藏同环比
        showModalData: false,
        // 仅下载，不选指标
        isOnlyDown: true,
        ...downConfig,
      },
      // 表格列配置
      componentMetrics: metricsList,
      // 自定义列
      customColumnsBeforeDim: [...customColumns],
      customColumnsAfterMetrics: [...autoColumn],
      // 额外请求参数
      otherParams,
      getParams: formatParams,
      ...paramsConfig,
    },
    ...params,
  };

  // 自定义传参
  if (activeRadio && radioValueKey) {
    newProps.config.otherParams = {
      [radioValueKey]: value === 'game' ? 'value' : radioValue,
      ...otherParams,
    };
  }

  if (filterConfig) {
    newProps.config.filterConfig = filterConfig;
  }

  return (
    <div
      ref={tableRef}
      className={classNames('dbb-table-container', {
        'dbb-table-container-not-scroll': !isScroll,
      })}
    >
      {filterVal !== null && <OverallTableCo {...newProps} />}
    </div>
  );
}
